import { createTheme, alpha } from '@mui/material/styles';

export const createMaterialYouTheme = (mode = 'light') => {
  // Material You colors
  const primaryColor = mode === 'light' ? '#006a6a' : '#4fd8d8';
  const secondaryColor = mode === 'light' ? '#7759cf' : '#cdbdff';
  
  return createTheme({
    palette: {
      mode,
      primary: {
        main: primaryColor,
        light: mode === 'light' ? '#48d4d4' : '#83efef',
        dark: mode === 'light' ? '#004f50' : '#00979a',
        contrastText: mode === 'light' ? '#ffffff' : '#003738',
      },
      secondary: {
        main: secondaryColor,
        light: mode === 'light' ? '#ae95ff' : '#e5dfff',
        dark: mode === 'light' ? '#5e42a6' : '#9a88cb',
        contrastText: mode === 'light' ? '#ffffff' : '#1e192b',
      },
      background: {
        default: mode === 'light' ? '#f5fafa' : '#191c1c',
        paper: mode === 'light' ? '#ffffff' : '#242929',
        subtle: mode === 'light' ? '#ebf5f5' : '#2c3131',
      },
      text: {
        primary: mode === 'light' ? '#191c1c' : '#e1e3e3',
        secondary: mode === 'light' ? '#414646' : '#bfc0c0',
      },
      action: {
        hover: alpha(primaryColor, 0.08),
        selected: alpha(primaryColor, 0.16),
      },
    },
    typography: {
      fontFamily: '"Google Sans", "Roboto", "Helvetica", "Arial", sans-serif',
      h1: { fontWeight: 400 },
      h2: { fontWeight: 400 },
      h3: { fontWeight: 400 },
      h4: { fontWeight: 500 },
      h5: { fontWeight: 500 },
      h6: { fontWeight: 500 },
      button: {
        textTransform: 'none',
        fontWeight: 500,
      },
    },
    shape: {
      borderRadius: 16,
    },
    components: {
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: 20,
            padding: '10px 24px',
            textTransform: 'none',
            fontFamily: '"Google Sans", "Roboto", "Helvetica", "Arial", sans-serif',
            fontWeight: 500,
            letterSpacing: 0.1,
            transition: 'all 0.2s',
          },
          contained: {
            boxShadow: 'none',
            '&:hover': {
              boxShadow: mode === 'light'
                ? '0 1px 3px 0 rgba(0,0,0,0.1), 0 1px 2px 0 rgba(0,0,0,0.06)'
                : '0 1px 3px 0 rgba(0,0,0,0.2), 0 1px 2px 0 rgba(0,0,0,0.1)',
            },
          },
          outlined: {
            borderWidth: 1.5,
            '&:hover': {
              borderWidth: 1.5,
              backgroundColor: alpha(primaryColor, 0.04),
            },
          },
        },
      },
      MuiAppBar: {
        defaultProps: {
          elevation: 0,
        },
        styleOverrides: {
          root: {
            backgroundColor: mode === 'light' ? '#ffffff' : '#242929',
            color: mode === 'light' ? '#191c1c' : '#e1e3e3',
            borderBottom: `1px solid ${mode === 'light' ? '#e0e5e5' : '#303535'}`,
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: 24,
            overflow: 'hidden',
            border: `1px solid ${mode === 'light' ? '#e0e5e5' : '#303535'}`,
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': {
              boxShadow: mode === 'light'
                ? '0 4px 20px 0 rgba(0,0,0,0.05), 0 1px 3px 0 rgba(0,0,0,0.03)'
                : '0 4px 20px 0 rgba(0,0,0,0.1), 0 1px 3px 0 rgba(0,0,0,0.05)',
            },
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            fontFamily: '"Google Sans", "Roboto", "Helvetica", "Arial", sans-serif',
            fontWeight: 500,
          },
          filled: {
            backgroundColor: mode === 'light' ? alpha(primaryColor, 0.12) : alpha(primaryColor, 0.24),
            color: mode === 'light' ? primaryColor : '#e1e3e3',
            '&:hover': {
              backgroundColor: mode === 'light' ? alpha(primaryColor, 0.2) : alpha(primaryColor, 0.32),
            },
          },
        },
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-root': {
              borderRadius: 12,
            },
          },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            fontWeight: 500,
            letterSpacing: 0.1,
          },
        },
      },
    },
  });
};